import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  Observable,
  FetchResult,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import { getDeviceId } from '../utils/deviceId';

const API_URL = import.meta.env.VITE_GRAPHQL_URL || 'http://localhost:8787/graphql';

// Track if we're currently refreshing to prevent multiple simultaneous refreshes
let isRefreshing = false;
let pendingRequests: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

/**
 * Process pending requests after token refresh
 */
function resolvePendingRequests() {
  pendingRequests.forEach(({ resolve }) => resolve(null));
  pendingRequests = [];
}

/**
 * Reject pending requests on refresh failure
 */
function rejectPendingRequests(error: unknown) {
  pendingRequests.forEach(({ reject }) => reject(error));
  pendingRequests = [];
}

/**
 * Refresh the access token using the stored refresh token
 * Returns true if successful, false otherwise
 */
async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem('refreshToken');
  
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          mutation RefreshToken($token: String!) {
            refreshToken(token: $token) {
              success
              _accessToken
              _refreshToken
            }
          }
        `,
        variables: { token: refreshToken },
      }),
    });

    const result = await response.json();

    if (result.data?.refreshToken?.success) {
      // Store new tokens
      if (result.data.refreshToken._accessToken) {
        localStorage.setItem('accessToken', result.data.refreshToken._accessToken);
      }
      if (result.data.refreshToken._refreshToken) {
        localStorage.setItem('refreshToken', result.data.refreshToken._refreshToken);
      }
      return true;
    }

    // Refresh failed - clear tokens
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    return false;
  } catch (error) {
    console.error('[Token refresh error]:', error);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    return false;
  }
}

// HTTP link
const httpLink = createHttpLink({
  uri: API_URL,
  credentials: 'include', // Include cookies
});

// Auth link - add access token and device ID to headers
const authLink = setContext((_, { headers }) => {
  // Get token from localStorage
  const token = localStorage.getItem('accessToken');
  // Get device ID for device revocation enforcement
  const deviceId = getDeviceId();

  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(deviceId ? { 'x-device-id': deviceId } : {}),
    },
  };
});

// Callback for when device is revoked - will be set by AuthProvider
let onDeviceRevoked: (() => void) | null = null;

// Callback for when session expires (refresh token invalid) - will be set by AuthProvider
let onSessionExpired: (() => void) | null = null;

export function setDeviceRevokedCallback(callback: () => void): void {
  onDeviceRevoked = callback;
}

export function clearDeviceRevokedCallback(): void {
  onDeviceRevoked = null;
}

export function setSessionExpiredCallback(callback: () => void): void {
  onSessionExpired = callback;
}

export function clearSessionExpiredCallback(): void {
  onSessionExpired = null;
}

// Error link - handle auth errors with automatic token refresh
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === 'DEVICE_REVOKED') {
        // Device was revoked - trigger logout and show message
        console.error('[GraphQL error]: Device revoked');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        if (onDeviceRevoked) {
          onDeviceRevoked();
        }
        return;
      }

      if (err.extensions?.code === 'UNAUTHENTICATED') {
        // Don't try to refresh if this is already a refresh token request
        if (operation.operationName === 'RefreshToken') {
          console.error('[GraphQL error]: Refresh token invalid');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          if (onSessionExpired) {
            onSessionExpired();
          }
          return;
        }

        // Token expired - try to refresh
        return new Observable<FetchResult>((observer) => {
          (async () => {
            try {
              // If already refreshing, wait for it to complete
              if (isRefreshing) {
                await new Promise((resolve, reject) => {
                  pendingRequests.push({ resolve, reject });
                });
              } else {
                isRefreshing = true;
                const success = await refreshAccessToken();
                isRefreshing = false;

                if (!success) {
                  // Refresh failed - session expired
                  rejectPendingRequests(new Error('Session expired'));
                  if (onSessionExpired) {
                    onSessionExpired();
                  }
                  observer.error(err);
                  return;
                }

                // Refresh succeeded - resolve pending requests
                resolvePendingRequests();
              }

              // Retry the failed request with new token
              const newToken = localStorage.getItem('accessToken');
              const oldHeaders = operation.getContext().headers;
              operation.setContext({
                headers: {
                  ...oldHeaders,
                  Authorization: newToken ? `Bearer ${newToken}` : '',
                },
              });

              // Retry the operation
              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
            } catch (refreshError) {
              observer.error(refreshError);
            }
          })();
        });
      }

      console.error(`[GraphQL error]: ${err.message}`);
    }
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError.message}`);
  }
});

// Apollo Client instance
export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          vaultItems: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      VaultItem: {
        keyFields: ['id'],
      },
      User: {
        keyFields: ['id'],
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
